import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { financialSnapshotAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import {
  Loader2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Receipt,
  Users,
  Briefcase,
  Wrench,
  PartyPopper,
  Building2,
  Headphones,
  Megaphone,
  PiggyBank,
  Landmark,
  AlertCircle,
} from 'lucide-react';

interface DREData {
  year: number;
  monthsClosed: number;
  totalRevenue: number;
  totalNetProfit: number;
  profitMargin: number;
  consolidatedData: {
    totalGrossRevenue: number;
    totalFees: number;
    totalNetRevenue: number;
    agencyProfit: number;
    totalOperationalRevenue: number;
    totalCommissions: number;
    totalEmployeeCost: number;
    operationalCosts: {
      tools: number;
      events: number;
      office: number;
      services: number;
      marketing: number;
      refunds: number;
      total: number;
    };
    taxAmount: number;
    totalCostsAndExpenses: number;
    netProfit: number;
    profitAdvances: number;
    profitAfterAdvances: number;
    partnerDebtPayments: number;
    retainedProfit: number;
  };
  monthlyBreakdown: Array<{
    month: number;
    year: number;
    monthName: string;
    totalRevenue: number;
    netProfit: number;
    totalCost: number;
    profitMargin: number;
  }>;
}

export default function DREPage() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [breakdownTab, setBreakdownTab] = useState<'table' | 'chart'>('table');

  const { data: dreResponse, isLoading } = useQuery({
    queryKey: ['dre-data', selectedYear],
    queryFn: async () => {
      const response = await financialSnapshotAPI.getDREByYear(selectedYear);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const dre: DREData = dreResponse?.data || {
    year: selectedYear,
    monthsClosed: 0,
    totalRevenue: 0,
    totalNetProfit: 0,
    profitMargin: 0,
    consolidatedData: {
      totalGrossRevenue: 0,
      totalFees: 0,
      totalNetRevenue: 0,
      agencyProfit: 0,
      totalOperationalRevenue: 0,
      totalCommissions: 0,
      totalEmployeeCost: 0,
      operationalCosts: {
        tools: 0,
        events: 0,
        office: 0,
        services: 0,
        marketing: 0,
        refunds: 0,
        total: 0,
      },
      taxAmount: 0,
      totalCostsAndExpenses: 0,
      netProfit: 0,
      profitAdvances: 0,
      profitAfterAdvances: 0,
      partnerDebtPayments: 0,
      retainedProfit: 0,
    },
    monthlyBreakdown: [],
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">DRE - Demonstração do Resultado do Exercício</h1>
          <p className="text-muted-foreground">
            Visão consolidada anual ({dre.monthsClosed} {dre.monthsClosed === 1 ? 'mês' : 'meses'} fechado{dre.monthsClosed === 1 ? '' : 's'})
          </p>
        </div>

        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {dre.monthsClosed === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              Nenhum mês fechado em {selectedYear}. Feche pelo menos um mês para visualizar o DRE.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Métricas Principais */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Receita Total */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Receita Total
                    </p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                      {formatCurrency(dre.totalRevenue)}
                    </p>
                    <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-2">
                      Receita Operacional Total do Ano
                    </p>
                  </div>
                  <div className="p-4 bg-blue-200 dark:bg-blue-900/40 rounded-lg">
                    <DollarSign className="h-8 w-8 text-blue-700 dark:text-blue-300" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lucro Líquido Total */}
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 border-green-200 dark:border-green-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">
                      Lucro Líquido Total
                    </p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                      {formatCurrency(dre.totalNetProfit)}
                    </p>
                    <p className="text-xs text-green-600/70 dark:text-green-400/70 mt-2">
                      Lucro antes de antecipações
                    </p>
                  </div>
                  <div className="p-4 bg-green-200 dark:bg-green-900/40 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-green-700 dark:text-green-300" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Margem de Lucro */}
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-purple-800 dark:text-purple-300">
                      Margem de Lucro
                    </p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                      {dre.profitMargin.toFixed(2)}%
                    </p>
                    <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-2">
                      (Lucro Líquido / Receita Total) × 100
                    </p>
                  </div>
                  <div className="p-4 bg-purple-200 dark:bg-purple-900/40 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-purple-700 dark:text-purple-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Métricas Médias Mensais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Receita Média Mensal */}
            <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-950/30 dark:to-cyan-900/20 border-cyan-200 dark:border-cyan-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-cyan-800 dark:text-cyan-300">
                      Receita Média Mensal
                    </p>
                    <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 mt-2">
                      {formatCurrency(dre.totalRevenue / dre.monthsClosed)}
                    </p>
                    <p className="text-xs text-cyan-600/70 dark:text-cyan-400/70 mt-2">
                      Média de {dre.monthsClosed} {dre.monthsClosed === 1 ? 'mês' : 'meses'}
                    </p>
                  </div>
                  <div className="p-4 bg-cyan-200 dark:bg-cyan-900/40 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-cyan-700 dark:text-cyan-300" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Despesa Média Mensal */}
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/30 dark:to-orange-900/20 border-orange-200 dark:border-orange-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                      Despesa Média Mensal
                    </p>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-2">
                      {formatCurrency(dre.consolidatedData.totalCostsAndExpenses / dre.monthsClosed)}
                    </p>
                    <p className="text-xs text-orange-600/70 dark:text-orange-400/70 mt-2">
                      Média de {dre.monthsClosed} {dre.monthsClosed === 1 ? 'mês' : 'meses'}
                    </p>
                  </div>
                  <div className="p-4 bg-orange-200 dark:bg-orange-900/40 rounded-lg">
                    <TrendingDown className="h-8 w-8 text-orange-700 dark:text-orange-300" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lucro Médio Mensal */}
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 border-emerald-200 dark:border-emerald-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                      Lucro Médio Mensal
                    </p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-2">
                      {formatCurrency(dre.totalNetProfit / dre.monthsClosed)}
                    </p>
                    <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70 mt-2">
                      Média de {dre.monthsClosed} {dre.monthsClosed === 1 ? 'mês' : 'meses'}
                    </p>
                  </div>
                  <div className="p-4 bg-emerald-200 dark:bg-emerald-900/40 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-emerald-700 dark:text-emerald-300" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Prejuízos e Reembolsos - Média Mensal */}
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-purple-800 dark:text-purple-300">
                      Prejuízos e Reembolsos
                    </p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                      {formatCurrency(dre.consolidatedData.operationalCosts.refunds / dre.monthsClosed)}
                    </p>
                    <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-2">
                      Média de {dre.monthsClosed} {dre.monthsClosed === 1 ? 'mês' : 'meses'}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-200 dark:bg-purple-900/40 rounded-lg">
                    <AlertCircle className="h-8 w-8 text-purple-700 dark:text-purple-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* DRE Consolidado */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold mb-6">DRE Consolidado - {selectedYear}</h2>

              <div className="space-y-4">
                {/* 1. Receita Operacional Bruta */}
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="font-semibold">1. Receita Operacional Bruta</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(dre.consolidatedData.totalGrossRevenue)}
                  </span>
                </div>

                {/* 2. (-) Deduções da Receita */}
                <div className="flex justify-between items-center py-3 border-b pl-4">
                  <span className="text-muted-foreground">2. (-) Deduções (Taxas Mercado Pago)</span>
                  <span className="text-red-600">
                    ({formatCurrency(dre.consolidatedData.totalFees)})
                  </span>
                </div>

                {/* 3. (=) Receita Operacional Líquida de Vendas */}
                <div className="flex justify-between items-center py-3 border-b bg-gray-50 dark:bg-gray-900 px-4">
                  <span className="font-semibold">3. (=) Receita Operacional Líquida de Vendas</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(dre.consolidatedData.totalNetRevenue)}
                  </span>
                </div>

                {/* 4. (+) Outras Receitas Operacionais */}
                <div className="flex justify-between items-center py-3 border-b pl-4">
                  <span className="text-muted-foreground">4. (+) Lucro de Serviços de Agência</span>
                  <span className="text-green-600">
                    {formatCurrency(dre.consolidatedData.agencyProfit)}
                  </span>
                </div>

                {/* 5. (=) Receita Operacional Total */}
                <div className="flex justify-between items-center py-3 border-b bg-blue-50 dark:bg-blue-950/20 px-4">
                  <span className="font-bold text-lg">5. (=) Receita Operacional Total</span>
                  <span className="font-bold text-lg text-blue-600">
                    {formatCurrency(dre.consolidatedData.totalOperationalRevenue)}
                  </span>
                </div>

                {/* 6. (-) Custos e Despesas Operacionais */}
                <div className="py-3 border-b">
                  <div className="font-semibold mb-3">6. (-) Custos e Despesas Operacionais</div>

                  <div className="space-y-2 pl-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-teal-600" />
                        <span className="text-sm">Comissões de Colaboradores</span>
                      </div>
                      <span className="text-sm text-red-600">
                        ({formatCurrency(dre.consolidatedData.totalCommissions)})
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-amber-600" />
                        <span className="text-sm">Custo com Pessoal</span>
                      </div>
                      <span className="text-sm text-red-600">
                        ({formatCurrency(dre.consolidatedData.totalEmployeeCost)})
                      </span>
                    </div>

                    {/* Custos Operacionais Detalhados */}
                    <div className="pl-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-3 w-3 text-blue-600" />
                          <span className="text-xs text-muted-foreground">Ferramentas</span>
                        </div>
                        <span className="text-xs text-red-600">
                          ({formatCurrency(dre.consolidatedData.operationalCosts.tools)})
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <PartyPopper className="h-3 w-3 text-pink-600" />
                          <span className="text-xs text-muted-foreground">Eventos</span>
                        </div>
                        <span className="text-xs text-red-600">
                          ({formatCurrency(dre.consolidatedData.operationalCosts.events)})
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3 w-3 text-slate-600" />
                          <span className="text-xs text-muted-foreground">Escritório</span>
                        </div>
                        <span className="text-xs text-red-600">
                          ({formatCurrency(dre.consolidatedData.operationalCosts.office)})
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Headphones className="h-3 w-3 text-cyan-600" />
                          <span className="text-xs text-muted-foreground">Serviços</span>
                        </div>
                        <span className="text-xs text-red-600">
                          ({formatCurrency(dre.consolidatedData.operationalCosts.services)})
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Megaphone className="h-3 w-3 text-orange-600" />
                          <span className="text-xs text-muted-foreground">Marketing</span>
                        </div>
                        <span className="text-xs text-red-600">
                          ({formatCurrency(dre.consolidatedData.operationalCosts.marketing)})
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-3 w-3 text-purple-600" />
                          <span className="text-xs text-muted-foreground">Prejuízos e Reembolsos</span>
                        </div>
                        <span className="text-xs text-red-600">
                          ({formatCurrency(dre.consolidatedData.operationalCosts.refunds)})
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-red-600" />
                        <span className="text-sm">Impostos (Simples Nacional)</span>
                      </div>
                      <span className="text-sm text-red-600">
                        ({formatCurrency(dre.consolidatedData.taxAmount)})
                      </span>
                    </div>
                  </div>
                </div>

                {/* 7. (=) Total de Custos e Despesas */}
                <div className="flex justify-between items-center py-3 border-b bg-red-50 dark:bg-red-950/20 px-4">
                  <span className="font-bold text-lg">7. (=) Total de Custos e Despesas</span>
                  <span className="font-bold text-lg text-red-600">
                    ({formatCurrency(dre.consolidatedData.totalCostsAndExpenses)})
                  </span>
                </div>

                {/* 8. (=) Lucro Líquido do Exercício */}
                <div className="flex justify-between items-center py-4 border-b bg-green-50 dark:bg-green-950/20 px-4">
                  <span className="font-bold text-xl">8. (=) Lucro Líquido do Exercício</span>
                  <span className="font-bold text-xl text-green-600">
                    {formatCurrency(dre.consolidatedData.netProfit)}
                  </span>
                </div>

              </div>

              {/* Destinação do Lucro: fora da DRE (não são custos, só o destino do lucro já apurado) */}
              <div className="mt-8 space-y-1">
                <div className="mb-3">
                  <h3 className="text-lg font-bold">Destinação do Lucro</h3>
                  <p className="text-sm text-muted-foreground">
                    Para onde foi o lucro líquido do ano. Não são custos e não alteram o resultado acima.
                  </p>
                </div>

                <div className="flex justify-between items-center py-3 border-b px-4">
                  <span className="font-semibold">Lucro Líquido do Exercício</span>
                  <span className="font-semibold">{formatCurrency(dre.consolidatedData.netProfit)}</span>
                </div>

                {/* Antecipações só existiram até o corte do caixa */}
                {dre.consolidatedData.profitAdvances > 0 && (
                  <div className="flex justify-between items-center py-3 border-b pl-4">
                    <div className="flex items-center gap-2">
                      <PiggyBank className="h-4 w-4 text-purple-600" />
                      <span className="text-muted-foreground">(-) Antecipações de Lucro aos Sócios</span>
                    </div>
                    <span className="text-red-600">
                      ({formatCurrency(dre.consolidatedData.profitAdvances)})
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center py-3 border-b pl-4">
                  <div className="flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-red-600" />
                    <span className="text-muted-foreground">(-) Pagamentos da Dívida com Sócios</span>
                  </div>
                  <span className="text-red-600">
                    ({formatCurrency(dre.consolidatedData.partnerDebtPayments)})
                  </span>
                </div>

                <div className="flex justify-between items-center py-4 bg-blue-50 dark:bg-blue-950/20 px-4 rounded-lg">
                  <span className="font-bold text-xl">(=) Resultado Retido no Caixa</span>
                  <span className={`font-bold text-xl ${dre.consolidatedData.retainedProfit < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                    {formatCurrency(dre.consolidatedData.retainedProfit)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Breakdown Mensal */}
          {dre.monthlyBreakdown.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Breakdown Mensal</h2>

                  {/* Tabs */}
                  <div className="flex gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                    <button
                      onClick={() => setBreakdownTab('table')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        breakdownTab === 'table'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      Tabela
                    </button>
                    <button
                      onClick={() => setBreakdownTab('chart')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        breakdownTab === 'chart'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      Gráfico
                    </button>
                  </div>
                </div>

                {/* Tabela */}
                {breakdownTab === 'table' && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">Mês</th>
                          <th className="text-right py-3 px-4">Receita Total</th>
                          <th className="text-right py-3 px-4">Custo Total</th>
                          <th className="text-right py-3 px-4">Lucro Líquido</th>
                          <th className="text-right py-3 px-4">Margem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dre.monthlyBreakdown.map((month) => (
                          <tr key={month.month} className="border-b hover:bg-gray-50 dark:hover:bg-gray-900">
                            <td className="py-3 px-4 font-medium">{month.monthName}</td>
                            <td className="text-right py-3 px-4">{formatCurrency(month.totalRevenue)}</td>
                            <td className="text-right py-3 px-4 text-red-600">
                              {formatCurrency(month.totalCost)}
                            </td>
                            <td className="text-right py-3 px-4 text-green-600">
                              {formatCurrency(month.netProfit)}
                            </td>
                            <td className="text-right py-3 px-4">
                              <span className={month.profitMargin > 0 ? 'text-green-600' : 'text-red-600'}>
                                {month.profitMargin.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Gráfico */}
                {breakdownTab === 'chart' && (
                  <div className="space-y-4">
                    <div className="h-96 relative">
                      <svg width="100%" height="100%" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid meet">
                        {/* Grid horizontal */}
                        {[0, 1, 2, 3, 4].map((i) => (
                          <g key={i}>
                            <line
                              x1="80"
                              y1={80 + i * 70}
                              x2="950"
                              y2={80 + i * 70}
                              stroke="currentColor"
                              strokeWidth="1"
                              className="text-gray-200 dark:text-gray-700"
                              strokeDasharray="4"
                            />
                          </g>
                        ))}

                        {/* Eixos */}
                        <line x1="80" y1="360" x2="950" y2="360" stroke="currentColor" className="text-gray-400" strokeWidth="2" />
                        <line x1="80" y1="80" x2="80" y2="360" stroke="currentColor" className="text-gray-400" strokeWidth="2" />

                        {(() => {
                          const sortedData = [...dre.monthlyBreakdown].sort((a, b) => a.month - b.month);
                          const maxValue = Math.max(
                            ...sortedData.map((m) => Math.max(m.totalRevenue, m.totalCost, m.netProfit))
                          );
                          const padding = maxValue * 0.1;
                          const yMax = maxValue + padding;

                          const pointsRevenue = sortedData.map((month, i) => ({
                            x: 80 + ((950 - 80) / (sortedData.length - 1)) * i,
                            y: 360 - ((month.totalRevenue / yMax) * 280),
                            value: month.totalRevenue,
                          }));

                          const pointsCost = sortedData.map((month, i) => ({
                            x: 80 + ((950 - 80) / (sortedData.length - 1)) * i,
                            y: 360 - ((month.totalCost / yMax) * 280),
                            value: month.totalCost,
                          }));

                          const pointsProfit = sortedData.map((month, i) => ({
                            x: 80 + ((950 - 80) / (sortedData.length - 1)) * i,
                            y: 360 - ((month.netProfit / yMax) * 280),
                            value: month.netProfit,
                          }));

                          const createPath = (points: typeof pointsRevenue) => {
                            return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                          };

                          return (
                            <>
                              {/* Labels do eixo Y */}
                              {[0, 1, 2, 3, 4].map((i) => {
                                const value = yMax - (yMax / 4) * i;
                                return (
                                  <text
                                    key={i}
                                    x="70"
                                    y={80 + i * 70}
                                    textAnchor="end"
                                    className="text-xs fill-gray-600 dark:fill-gray-400"
                                    dominantBaseline="middle"
                                  >
                                    {formatCurrency(value).replace('R$', '').trim()}
                                  </text>
                                );
                              })}

                              {/* Labels do eixo X */}
                              {sortedData.map((month, i) => (
                                <text
                                  key={i}
                                  x={80 + ((950 - 80) / (sortedData.length - 1)) * i}
                                  y="380"
                                  textAnchor="middle"
                                  className="text-xs fill-gray-600 dark:fill-gray-400"
                                >
                                  {month.monthName.substring(0, 3)}
                                </text>
                              ))}

                              {/* Linha de Receita (Azul) */}
                              <path
                                d={createPath(pointsRevenue)}
                                fill="none"
                                stroke="#3B82F6"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {pointsRevenue.map((point, i) => (
                                <circle key={i} cx={point.x} cy={point.y} r="5" fill="#3B82F6" />
                              ))}

                              {/* Linha de Custo (Vermelho) */}
                              <path
                                d={createPath(pointsCost)}
                                fill="none"
                                stroke="#EF4444"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {pointsCost.map((point, i) => (
                                <circle key={i} cx={point.x} cy={point.y} r="5" fill="#EF4444" />
                              ))}

                              {/* Linha de Lucro (Verde) */}
                              <path
                                d={createPath(pointsProfit)}
                                fill="none"
                                stroke="#10B981"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {pointsProfit.map((point, i) => (
                                <circle key={i} cx={point.x} cy={point.y} r="5" fill="#10B981" />
                              ))}
                            </>
                          );
                        })()}
                      </svg>
                    </div>

                    {/* Legenda */}
                    <div className="flex items-center justify-center gap-6 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium">Receita Total</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium">Custo Total</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Lucro Líquido</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
